import forge from 'apis/forge';
import {
	getClipboardItems,
	getFigmaLink,
	getFrameIdsFromText,
	getMoonshotClipboardFromText,
	hasPastableItems,
	pasteFigmaLinkAsFrame,
	pasteImageAsFrame,
	pasteMoonshotAsFrame,
	putTextOnClipboard,
} from 'clipboard';
import * as api from 'dragonmouse-shared/api/forge';
import { getBoundingBox, Rect } from 'dragonmouse-shared/geometry';
import { DesignSystem, Frame, FrameStatus, FrameType, Scene } from 'dragonmouse-shared/interfaces';
import { toast } from 'react-toastify';
import { selectActiveScene, selectFrames, selectUser, selectIsFreeTier } from 'redux/db/reducers';
import {
	canEdit,
	canExportSvg,
	Direction,
	getAdjacentFrame,
	getChat,
	getFrame,
	getFrameBounds,
	getFrameExportHtml,
	getFrameImage,
	getFrameSvg,
	isFramePreviewable,
} from 'redux/db/util';
import {
	centerCanvasAroundSceneBoundingBox,
	setActiveSceneChatId,
	setActiveLeftPanelTab,
	setActiveSceneId,
	setFrameCentered,
	setFrameIdToScrollTo,
	setHovered,
	setPanelsVisible,
	setPendingChatInput,
	setSelected,
	zoomAroundCanvasCenter,
	zoomSetAroundCanvasCenter,
} from 'redux/mode/actions';
import { Pick } from 'redux/mode/classes';
import { executeCommand, redo, undo } from 'redux/mode/commandManager';
import {
	DeleteCommand,
	UpdateChatCommand,
	UpdateDesignSystemCommand,
	UpdateFrameCommand,
	UpdateSceneCommand,
} from 'redux/mode/commands';
import { LeftPanelTab, ZoomType } from 'redux/mode/reducers';
import { displayPromptModal, displayUpgradeModal, getSelectedFrameIds } from 'redux/mode/util';
import { selectSelectedFrames } from 'redux/reducers';
import store from 'redux/store';
import * as routes from 'routes';
import { Target, TargetCanvas } from 'transfer/Target';
import { generateShortcutText, navigate } from 'utils';
import { v4 as uuid } from 'uuid';
import {
	trackCreativeAction,
	trackDesignSystemAction,
	trackMiscAction,
	trackOutputAction,
	trackSceneAction,
} from '../analytics';
import { displayViewOnlyModal } from '../redux/mode/util';

export abstract class MenuItem {
	constructor(public readonly kind: string) {}

	// Returns a modified version of this MenuItem, which triggers the provided
	// callback AFTER it is selected. Items without a callback will not be modified.
	// NOTE: i thought about putting this only on MenuItemAction but this feels a bit cleaner, dunno - ss
	addCallback(_callback: () => void): MenuItem {
		return this;
	}
}

export class MenuItemDivider extends MenuItem {
	constructor() {
		super('MenuItemDivider');
	}
}

export class MenuItemAction extends MenuItem {
	constructor(
		public readonly label: string,
		public readonly shortcut: string,
		public readonly callback: () => any,
		public readonly disabled?: boolean,
		public readonly showCheckmark?: boolean
	) {
		super('MenuItemAction');
	}

	addCallback(callback: () => void): MenuItem {
		return new MenuItemAction(
			this.label,
			this.shortcut,
			() => {
				// first call the existing callbacks (this.callback)
				this.callback();
				// then call the new callback
				callback();
			},
			this.disabled,
			this.showCheckmark
		);
	}

	static createUndo(): MenuItemAction {
		return new this(
			'Undo',
			generateShortcutText('Z', { ctrl: true }),
			async () => {
				undo();
			},
			store.getState().mode.commandStack.length === 0
		);
	}

	static createRedo(): MenuItemAction {
		return new this(
			'Redo',
			generateShortcutText('Z', { ctrl: true, shift: true }),
			async () => {
				redo();
			},
			store.getState().mode.undoneCommands.length === 0
		);
	}

	static createZoomIn(): MenuItemAction {
		return new this('Zoom in', generateShortcutText('+', { ctrl: true }), async () => {
			store.dispatch(zoomAroundCanvasCenter(ZoomType.ZOOM_IN));
		});
	}

	static createZoomOut(): MenuItemAction {
		return new this('Zoom out', generateShortcutText('–', { ctrl: true }), async () => {
			store.dispatch(zoomAroundCanvasCenter(ZoomType.ZOOM_OUT));
		});
	}

	static createZoomReset(): MenuItemAction {
		return new this('Zoom to 100%', generateShortcutText('0', { ctrl: true }), async () => {
			store.dispatch(zoomSetAroundCanvasCenter(1.0));
		});
	}

	static createZoomToFitCanvas(): MenuItemAction {
		const frames = selectFrames(store.getState().db);
		return new this(
			'Zoom to fit canvas',
			generateShortcutText('1', { shift: true }),
			async () => {
				const bounds = getBoundingBox(frames.map((frame) => getFrameBounds(frame)));
				store.dispatch(
					centerCanvasAroundSceneBoundingBox({
						boundingBox: bounds,
						animated: true,
					})
				);
			},
			frames.length === 0
		);
	}

	static createZoomToFitSelection(): MenuItemAction {
		return new this(
			'Zoom to fit selection',
			generateShortcutText('3', { shift: true }),
			async () => {
				const selectedFrames = selectSelectedFrames(store.getState());
				const bounds = getBoundingBox(selectedFrames.map((frame) => getFrameBounds(frame)));
				store.dispatch(
					centerCanvasAroundSceneBoundingBox({
						boundingBox: bounds,
						animated: true,
						tightFit: true,
					})
				);
			},
			getSelectedFrameIds(store.getState().mode.selected).length === 0
		);
	}

	static createDeleteFrames(frameIds: string[]): MenuItemAction {
		return new this('Delete', 'Del', () => {
			const scene = selectActiveScene(store.getState().db, store.getState().mode.activeSceneId);
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			trackMiscAction('delete_frame');
			executeCommand(new DeleteCommand(frameIds));
		});
	}

	static createDeleteFrameElements(frameId: string, elementIds: string[]): MenuItemAction {
		return new this('Delete', '', () => {
			// store.dispatch(setSelected(null));
			// store.dispatch(setHovered(null));
			const req: api.DeleteFrameElementsRequest = {
				frameId,
				elementIds,
			};
			forge.deleteFrameElements.withToast()(req);
		});
	}

	static createAddFramesToChat(frameIds: string[]): MenuItemAction {
		return new this('Add to chat', '@', () => {
			const activeSceneChatId = store.getState().mode.activeSceneChatId;
			const pendingChatInputs = store.getState().mode.pendingChatInputs;

			if (!activeSceneChatId) {
				toast('Internal error: no active chat detected');
				return;
			}

			const pendingChatInput = pendingChatInputs[activeSceneChatId] || {
				frameIds: [],
				message: '',
			};

			// currently we don't have to check these as re-setting them should have no side effect
			// however this is cheap and more robust to check in case something changes
			const activeLeftPanelTab = store.getState().mode.activeLeftPanelTab;
			if (activeLeftPanelTab !== LeftPanelTab.CHAT) {
				store.dispatch(setActiveLeftPanelTab(LeftPanelTab.CHAT));
			}
			const panelsVisible = store.getState().mode.panelsVisible;
			if (!panelsVisible) {
				store.dispatch(setPanelsVisible(true));
			}

			const allFrameIds = [...pendingChatInput.frameIds, ...frameIds];
			store.dispatch(
				setPendingChatInput({
					chatId: activeSceneChatId,
					pendingChatInput: {
						...pendingChatInput,
						frameIds: allFrameIds,
					},
				})
			);

			// focus the chat input after a short delay
			// this is a filthy hack but seems to work, is safe, doesn't add much complexity - let's ship it! ss
			setTimeout(() => {
				const chatInput = document.querySelector(
					'textarea[data-chat-input="true"]'
				) as HTMLTextAreaElement;
				if (chatInput) {
					chatInput.focus();
				}
			}, 100);
		});
	}

	static createCenterFrame(frame: Frame): MenuItemAction {
		return new this('Focus frame', 'Dbl-click', async () => {
			trackMiscAction('focus_frame');
			store.dispatch(setSelected(new Pick(frame.id)));
			store.dispatch(setHovered(null));
			store.dispatch(
				centerCanvasAroundSceneBoundingBox({
					boundingBox: getFrameBounds(frame),
					animated: true,
					frameId: frame.id,
				})
			);
			store.dispatch(setFrameCentered(frame.id));
		});
	}

	static createNavFromFrame(frameId: string, direction: Direction): MenuItemAction {
		const frame = getAdjacentFrame(frameId, direction);
		return new this(
			`Navigate ${direction.toLowerCase()}`,
			'',
			() => {
				if (frame) {
					trackMiscAction('focus_arrow');
					MenuItemAction.createCenterFrame(frame).callback();
				}
			},
			!frame
		);
	}

	static createCopyFrameUrl(frames: Frame | Frame[]): MenuItemAction {
		return new this('Copy', generateShortcutText('C', { ctrl: true }), async () => {
			trackMiscAction('copy_frame');
			if (Array.isArray(frames)) {
				const urls = frames
					.map(
						(frame) =>
							`${window.location.origin}${routes.modifyCurrentPath({
								frameId: frame.id,
								edit: 'false',
							})}`
					)
					.join('\n');
				putTextOnClipboard(urls);
			} else {
				putTextOnClipboard(
					`${window.location.origin}${routes.modifyCurrentPath({
						frameId: frames.id,
						edit: 'false',
					})}`
				);
			}
		});
	}

	static createCopyFrameImage(frame: Frame): MenuItemAction {
		const user = selectUser(store.getState().db);
		const numberOfExports = user?.metadata.numberOfExports || 0;
		return new this(
			'Copy as PNG',
			'',
			async () => {
				trackOutputAction('copy_png');
				const promiseToCopy = new Promise<void>((resolve, reject) => {
					(async () => {
						try {
							const blob = await getFrameImage(frame);
							const clipboardItem = new ClipboardItem({
								'image/png': blob,
							});
							await navigator.clipboard.write([clipboardItem]);
							forge.updateUser({
								update: {
									id: user?.id,
									metadata: {
										numberOfExports: numberOfExports + 1,
										lastExportDate: new Date().toISOString(),
									},
								},
							});
						} catch (error) {
							console.error('Failed to copy screen:', error);
							throw error;
						}
					})()
						.then(() => resolve())
						.catch((error) => reject(error));
				});
				toast.promise(promiseToCopy, {
					pending: 'Copying PNG...',
					success: 'PNG copied to clipboard',
					error: 'Failed to copy PNG',
				});
			},
			frame.metadata.status !== FrameStatus.READY
		);
	}

	static createCopyFrameSvg(frame: Frame): MenuItemAction {
		const user = selectUser(store.getState().db);
		const numberOfExports = user?.metadata.numberOfExports || 0;
		return new this(
			'Copy as Figma SVG',
			'',
			async () => {
				trackOutputAction('copy_svg');
				const promiseToCopy = new Promise<void>((resolve, reject) => {
					(async () => {
						try {
							const blob = await getFrameSvg(frame);
							const svgText = await blob.text();
							const clipboardItem = new ClipboardItem({
								'text/plain': new Blob([svgText], { type: 'text/plain' }),
							});
							await navigator.clipboard.write([clipboardItem]);
							forge.updateUser({
								update: {
									id: user?.id,
									metadata: {
										numberOfExports: numberOfExports + 1,
										lastExportDate: new Date().toISOString(),
									},
								},
							});
						} catch (error) {
							console.error('Failed to copy screen:', error);
							throw error;
						}
					})()
						.then(() => resolve())
						.catch((error) => reject(error));
				});
				toast.promise(promiseToCopy, {
					pending: 'Copying SVG...',
					success: 'SVG copied to clipboard',
					error: 'Failed to copy SVG',
				});
			},
			frame.metadata.status !== FrameStatus.READY || !canExportSvg([frame])
		);
	}

	static createCopyFrameHtml(frame: Frame): MenuItemAction {
		const user = selectUser(store.getState().db);
		const isFreeTier = selectIsFreeTier(store.getState().db);
		const numberOfExports = user?.metadata.numberOfExports || 0;
		return new this(
			'Copy as HTML',
			'',
			async () => {
				if (isFreeTier) {
					displayUpgradeModal('HTML export');
					return;
				}
				trackOutputAction('copy_html');
				// An LLM adviused me thus - given the Safari issues we've had with this code,
				// we may wanna try it - ss
				// In studio/src/menu/MenuItem.ts around lines 417 to 463, remove the console.log
				// debug statements and harden the clipboard copy flow so it works when
				// ClipboardItem is not available: after obtaining htmlText, try to create and
				// write a ClipboardItem inside a try/catch; if ClipboardItem or
				// navigator.clipboard.write fails or is unavailable, fall back to
				// navigator.clipboard.writeText(htmlText) if available, and if that also fails,
				// fall back to a DOM-based copy (create a hidden textarea/input, set its value to
				// htmlText, select it, call document.execCommand('copy'), then remove the
				// element). Only update the user export counters after a successful copy, and
				// ensure all error branches reject so toast.promise can show failure.

				const promiseToCopy = new Promise<void>((resolve, reject) => {
					(async () => {
						try {
							const blob = await getFrameExportHtml(frame);
							const htmlText = await blob.text();
							const clipboardItem = new ClipboardItem({
								'text/html': new Blob([htmlText], { type: 'text/html' }),
								'text/plain': new Blob([htmlText], { type: 'text/plain' }),
							});
							await navigator.clipboard.write([clipboardItem]);
							forge.updateUser({
								update: {
									id: user?.id,
									metadata: {
										numberOfExports: numberOfExports + 1,
										lastExportDate: new Date().toISOString(),
									},
								},
							});
						} catch (error) {
							console.error('Failed to copy HTML:', error);
							throw error;
						}
					})()
						.then(() => resolve())
						.catch((error) => reject(error));
				});
				toast.promise(promiseToCopy, {
					pending: 'Copying HTML...',
					success: 'HTML copied to clipboard',
					error: 'Failed to copy HTML',
				});
			},
			frame.metadata.status !== FrameStatus.READY
		);
	}

	static createRetry(frame: Frame): MenuItemAction {
		// Currently, GENERATE_FROM_CHAT frames created after Nov 2025 have recipes and can be retried
		// Older GENERATE_FROM_CHAT frames don't have recipes; they will fail on the backend if retry is attempted
		//
		// Originally we did a check for a recipe, but the forge is no longer sending recipe entries over because they are a lot of data
		// So for now let's check the frame type. Older frames will fail on the backend. I think we can live with that
		// (in practice only recently generated frames tend to be retried)
		const canRetry = frame.metadata.type === FrameType.GENERATE_FROM_CHAT;

		return new this(
			'Retry generation',
			'',
			async () => {
				const scene = selectActiveScene(store.getState().db, store.getState().mode.activeSceneId);
				if (!canEdit(scene)) {
					displayViewOnlyModal();
					return;
				}
				trackCreativeAction('retry');
				try {
					const response = await forge.retryFrame.withToast()({ frameId: frame.id });
					if (!response.success) {
						toast.info('Frame cannot be retried');
					}
				} catch (e) {
					toast.error('Frame cannot be retried');
				}
			},
			!canRetry
		);
	}

	static async createPaste(target: Target): Promise<MenuItemAction> {
		let complete: (() => void) | null = null;

		const sceneId = store.getState().mode.activeSceneId;
		const figmaIntegrationEnabled = store.getState().mode.settings.figmaIntegrationEnabled;
		const clipboardItems = await getClipboardItems();
		if (
			target instanceof TargetCanvas &&
			sceneId &&
			(await hasPastableItems(clipboardItems, figmaIntegrationEnabled))
		) {
			complete = async () => {
				try {
					for (const item of clipboardItems) {
						for (const type of item.types) {
							if (type === 'image/png') {
								trackMiscAction('paste_image');
								pasteImageAsFrame(item, sceneId, target.scene);
							} else if (type === 'text/plain') {
								const text = await item.getType('text/plain');
								const textContent = await text.text();

								const figmaLink = getFigmaLink(textContent);
								const frameIds = getFrameIdsFromText(textContent);
								const moonshotClipboard = getMoonshotClipboardFromText(textContent);

								if (figmaLink && figmaIntegrationEnabled) {
									trackMiscAction('paste_figma_link');
									pasteFigmaLinkAsFrame(figmaLink, target.scene);
								} else if (frameIds.length > 0) {
									trackMiscAction('paste_frames');
									// Get all the frames to duplicate
									const frames = frameIds
										.map((id) => getFrame(id))
										.filter((f): f is Frame => f !== null);

									// If all the frames are found locally, use this duplicateFrame
									// which gives us nice optimistic updates.
									if (frames.length === frameIds.length) {
										const allBounds = frames.map(getFrameBounds);
										const minX = Math.min(...allBounds.map((b) => b.x));
										const minY = Math.min(...allBounds.map((b) => b.y));

										const duplicationTasks = frames.map((frame) => {
											const newFrameId = uuid();
											const frameBounds = getFrameBounds(frame);
											const relativeX = frameBounds.x - minX;
											const relativeY = frameBounds.y - minY;
											return {
												frame,
												newFrameId,
												x: Math.round(target.scene.x + (frames.length === 1 ? 0 : relativeX)),
												y: Math.round(target.scene.y + (frames.length === 1 ? 0 : relativeY)),
											};
										});

										await Promise.all(
											duplicationTasks.map(({ frame, newFrameId, x, y }) =>
												forge.duplicateFrame(frame, newFrameId, sceneId, x, y)
											)
										);
									} else {
										// Some frames are missing - this means we are either pasting in frames from a different scene
										// or pasting frames that were deleted
										const response: api.PasteFramesIntoSceneResponse =
											await forge.pasteFramesIntoScene({
												sceneId,
												frameIds,
												x: target.scene.x,
												y: target.scene.y,
											});

										if (response.skippedFrameIds.length > 0) {
											toast.warn(
												`Could not paste ${response.skippedFrameIds.length} frame(s) - access denied`
											);
										}
									}
								} else if (moonshotClipboard) {
									trackMiscAction('paste_moonshot');
									pasteMoonshotAsFrame(moonshotClipboard, sceneId, target.scene);
								}
							}
						}
					}
				} catch (error) {
					console.error('Failed to read clipboard contents:', error);
				}
			};
		}

		return new this(
			'Paste',
			generateShortcutText('v', { ctrl: true }),
			() => {
				complete?.();
			},
			!complete
		);
	}

	static async createPasteText(onPaste: (text: string) => void): Promise<MenuItemAction> {
		const clipboardItems = await getClipboardItems();
		const textItem = clipboardItems.find((item) => item.types.includes('text/plain'));
		let text = '';
		if (textItem) {
			const textType = await textItem.getType('text/plain');
			text = await textType.text();
		}
		return new this(
			'Paste',
			generateShortcutText('v', { ctrl: true }),
			() => {
				onPaste(text);
			},
			!text.length
		);
	}

	static createCopyText(text: string): MenuItemAction {
		return new this('Copy', generateShortcutText('c', { ctrl: true }), async () => {
			putTextOnClipboard(text);
		});
	}

	static createRenameScene(scene: Scene): MenuItemAction {
		return new this('Rename project...', '', () => {
			displayPromptModal({
				title: 'Rename project',
				description: 'Enter a new name for the project',
				submitLabel: 'Rename',
				initialValue: scene.title,
				onSubmit: async (value?: string) => {
					if (!value) return;
					trackSceneAction('rename');
					executeCommand(
						new UpdateSceneCommand(
							{ id: scene.id, title: scene.title },
							{ id: scene.id, title: value }
						)
					);
				},
			});
		});
	}

	static createRenameDesignSystem(designSystem: DesignSystem): MenuItemAction {
		return new this('Rename design system...', '', () => {
			displayPromptModal({
				title: 'Rename design system',
				description: 'Enter a new name for the design system',
				submitLabel: 'Rename',
				initialValue: designSystem.title,
				onSubmit: async (value?: string) => {
					if (!value) return;
					trackDesignSystemAction('rename');
					executeCommand(
						new UpdateDesignSystemCommand(
							{ id: designSystem.id, title: designSystem.title },
							{ id: designSystem.id, title: value }
						)
					);
				},
			});
		});
	}

	static createRenameFrame(frame: Frame): MenuItemAction {
		return new this('Rename frame...', '', async () => {
			displayPromptModal({
				title: 'Rename frame',
				description: 'Enter a new name for the frame',
				submitLabel: 'Rename',
				initialValue: frame.title,
				onSubmit: async (value?: string) => {
					if (!value) return;
					trackMiscAction('rename_frame');
					executeCommand(
						new UpdateFrameCommand(
							{ id: frame.id, title: frame.title },
							{ id: frame.id, title: value }
						)
					);
				},
			});
		});
	}

	static createReturnToProjectList(): MenuItemAction {
		return new this('Return to project list...', '', () => {
			trackSceneAction('return_to_scene_list');
			// there is a useEffect in App.tsx that navigates to the project page whenever activeSceneId is set
			// this useEffect ends up navigating us back to the project when we try to navigate to the home page!
			// so i clear the activeSceneId first. This is a quick hack that works for now.
			// Later I'd like to clean this up. Too many parties are responsible for navigating.
			store.dispatch(setActiveSceneId(null));
			navigate(routes.home());
		});
	}

	static createRenameChat(chatId: string): MenuItemAction {
		return new this('Rename chat...', '', async () => {
			const scene = selectActiveScene(store.getState().db, store.getState().mode.activeSceneId);
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			const chat = getChat(chatId);
			if (!chat) {
				toast.error('Could not find chat to rename');
				return;
			}

			displayPromptModal({
				title: 'Rename chat',
				description: 'Enter a new name for the chat',
				submitLabel: 'Rename',
				initialValue: chat.title,
				onSubmit: async (value?: string) => {
					if (!value) return;
					trackMiscAction('rename_chat');
					executeCommand(
						new UpdateChatCommand({ id: chatId, title: chat.title }, { id: chatId, title: value })
					);
				},
			});
		});
	}

	static createSelectFramesFromChat(chatId: string): MenuItemAction {
		return new this('Select frames from chat', '', async () => {
			// gather frames which (1) have chatId in their metadata.sourceChatId and (2) are not deleted
			const frames = selectFrames(store.getState().db).filter(
				(frame) => frame.metadata.sourceChatId === chatId
			);

			if (frames.length === 0) {
				toast.warning('No frames in this chat are on the canvas');
				return;
			}

			// get the bounds for these frames and zoom to fit
			const bounds: Rect[] = [];
			for (const frame of frames) {
				bounds.push(getFrameBounds(frame));
			}
			store.dispatch(
				centerCanvasAroundSceneBoundingBox({
					boundingBox: getBoundingBox(bounds),
					animated: true,
				})
			);

			store.dispatch(setSelected(frames.map((frame) => new Pick(frame.id))));
		});
	}

	static createScrollToFrameInChat(chatId: string, frameId?: string): MenuItemAction {
		return new this('Show in chat', '', async () => {
			const activeLeftPanelTab = store.getState().mode.activeLeftPanelTab;
			const panelsVisible = store.getState().mode.panelsVisible;

			// activate the chat panel if needed
			if (activeLeftPanelTab !== LeftPanelTab.CHAT) {
				store.dispatch(setActiveLeftPanelTab(LeftPanelTab.CHAT));
			}
			if (!panelsVisible) {
				store.dispatch(setPanelsVisible(true));
			}
			store.dispatch(setActiveSceneChatId(chatId));

			// scroll!
			// The scroll will happen in SceneConversation useEffect when the chat loads
			if (frameId) {
				store.dispatch(setFrameIdToScrollTo(frameId));
			}
		});
	}

	static createOpenPrototype(frameId: string): MenuItemAction {
		const frame = getFrame(frameId);
		return new this(
			'View prototype',
			'P',
			() => {
				trackMiscAction('load_view_prototype');
				navigate(routes.modifyCurrentPath({ frameId }));
			},
			!frame || !isFramePreviewable(frame)
		);
	}

	static createOpenPrototypeInNewTab(frame: Frame): MenuItemAction {
		return new this('View prototype in new tab', '', async () => {
			trackMiscAction('load_prototype_in_new_tab');
			window.open(
				`${window.location.origin}${routes.modifyCurrentPath({ frameId: frame.id })}`,
				'_blank'
			);
		});
	}
}
